import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Button,
  useTheme,
  Paper,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Terminal,
  CopyAll,
  ErrorOutline,
  CheckCircleOutline,
  EditOutlined,
  RestoreOutlined,
} from "@mui/icons-material";
import Editor from "@monaco-editor/react";
import Ajv from "ajv";
import addFormats from "ajv-formats";
import { jsonInputSchema } from "../schema/jsonSchemav2";

export default function JsonPreviewModal({
  open,
  onClose,
  jsonData,
  importJson = async () => {},
  modalStyle = null,
  title = "Preview JSON",
  isPing = false,
}) {
  const theme = useTheme();

  const styles = {
    modal: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: 800,
      bgcolor: "background.default",
      boxShadow: 24,
      p: 4,
      ...modalStyle,
    },
    logContainer: {
      mt: 2,
      p: 2,
      bgcolor: "background.paper",
      borderRadius: 1,
      border: `1px solid ${theme.palette.divider}`,
    },
  };

  const [editorValue, setEditorValue] = useState("");
  const [error, setError] = useState(null);
  const [parsed, setParsed] = useState(null);
  const editorRef = useRef();
  const originalValueRef = useRef("");

  const ajv = useMemo(() => {
    const instance = new Ajv({ allErrors: true, strict: false });
    addFormats(instance);
    instance.compile(jsonInputSchema);
    return instance;
  }, []);

  // carrega o JSON inicial quando o modal abre ou quando jsonData muda
  useEffect(() => {
    const initial = jsonData ? JSON.stringify(jsonData, null, 2) : "";
    originalValueRef.current = initial;
    setEditorValue(initial);
  }, [open, jsonData]);

  // valida sempre que o texto do editor mudar
  useEffect(() => {
    if (!editorValue) {
      setParsed(null);
      setError("Nenhum JSON carregado");
      return;
    }

    try {
      const data = JSON.parse(editorValue);

      // valida contra o schema
      let schemaToUse = jsonInputSchema;
      if (isPing && Array.isArray(jsonInputSchema.required)) {
        schemaToUse = {
          ...jsonInputSchema,
          required: jsonInputSchema.required.filter((r) => r !== "relations"),
        };
      }
      const valid = ajv.validate(schemaToUse, data);
      let validationError = valid
        ? null
        : ajv.errorsText(ajv.errors, { separator: "\n" });

      // regra extra: coordenadas duplicadas
      if (!validationError && Array.isArray(data.elements)) {
        const seen = new Set();
        let duplicate = null;
        let mapRoot = data.rootMapId;
        let elementsRoot = data.elements.filter((el) => el.mapId == mapRoot);
        for (const item of elementsRoot) {
          const coords = item?.elementConfig?.config?.coordenadas;
          if (coords) {
            const key = `${coords.lat}, ${coords.long}`;
            if (seen.has(key)) {
              duplicate = key;
              break;
            }
            seen.add(key);
          }
        }
        if (duplicate) {
          validationError = `Coordenadas duplicadas encontradas: ${duplicate}`;
        }
      }

      setParsed(data);
      setError(validationError);
    } catch (e) {
      setParsed(null);
      setError(`Erro de sintaxe JSON: ${e.message}`);
    }
  }, [editorValue, ajv]);

  const handleMount = (editor, monacoInstance) => {
    editorRef.current = editor;
    monacoInstance.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [
        {
          uri: "inmemory://user.schema.json",
          fileMatch: ["*"],
          schema: jsonInputSchema,
        },
      ],
    });
    editor.getAction("editor.action.formatDocument")?.run();
    editor.layout();
  };

  const handleFormat = () => {
    editorRef.current?.getAction("editor.action.formatDocument")?.run();
  };

  const handleReset = () => {
    setEditorValue(originalValueRef.current || "");
    setTimeout(() => handleFormat(), 0);
  };

  const copyLog = async () => {
    try {
      await navigator.clipboard.writeText(
        error ? String(error) : "JSON válido",
      );
    } catch (_) {}
  };

  const disabled = !editorValue;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="preview-json-modal-title"
      aria-describedby="preview-json-modal-description"
    >
      <Box sx={styles.modal}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography id="preview-json-modal-title" variant="h6" component="h2">
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <Tooltip title="Formatar JSON">
                <Button
                  size="small"
                  onClick={handleFormat}
                  startIcon={<EditOutlined />}
                  variant="outlined"
                >
                  Formatar
                </Button>
              </Tooltip>
              <Tooltip title="Restaurar conteúdo inicial">
                <Button
                  size="small"
                  onClick={handleReset}
                  startIcon={<RestoreOutlined />}
                  variant="outlined"
                >
                  Restaurar
                </Button>
              </Tooltip>
            </Box>
            {/*             <IconButton
              aria-label="close"
              onClick={onClose}
              size="small"
              sx={{ color: theme.palette.text.primary }}
            >
              <CloseIcon />
            </IconButton> */}
          </Box>
        </Box>

        <Box
          sx={{
            mt: 2,
            maxHeight: 350,
            overflow: "hidden",
            bgcolor: "background.paper",
            p: 0,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Editor
            key="monaco-editor"
            height="350px"
            defaultLanguage="json"
            onMount={handleMount}
            value={editorValue}
            onChange={(val) => setEditorValue(val ?? "")}
            theme={theme.palette.mode === "dark" ? "vs-dark" : "light"}
            options={{
              readOnly: false,
              formatOnPaste: true,
              renderValidationDecorations: "on",
              minimap: { enabled: true },
              scrollBeyondLastLine: false,
              formatOnType: true,
              wordWrap: "on",
              folding: true,
              lineNumbers: "on",
              automaticLayout: true,
            }}
          />
        </Box>

        {!disabled && (
          <Paper sx={styles.logContainer}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Terminal fontSize="small" />
                <Typography sx={{ fontWeight: 600 }}>Log</Typography>
              </Box>
              <Tooltip title="Copiar log">
                <IconButton onClick={copyLog}>
                  <CopyAll fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Divider sx={{ my: 1 }} />
            {error ? (
              <Box sx={{ display: "flex" }}>
                <ErrorOutline
                  sx={{
                    fontSize: 16,
                    color: theme.palette.error.main,
                    mt: 0.5,
                    mr: 0.5,
                  }}
                />
                <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>
                  {error}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex" }}>
                <CheckCircleOutline
                  sx={{
                    fontSize: 16,
                    color: theme.palette.success.main,
                    mt: 0.5,
                    mr: 0.5,
                  }}
                />
                <Typography sx={{ fontSize: 14 }}>
                  JSON válido e pronto para importação.
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
        >
          <Button variant="text" color="primary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={Boolean(error) || !parsed}
            onClick={async () => {
              if (!parsed) return;
              await importJson(parsed);
            }}
          >
            Importar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
